/**
 * time.ts
 * ----------------
 * Utility types and functions for timestamps.
 */

import * as moment from 'moment';

import {
    Last24Hours, Last30Days, Last48Hours, Last60Days, Last7Days, Last90Days, TimeRangeValue,
} from '../models/ui/status';

/**
 * Gets difference in milliseconds between two dates.
 * @param a First date
 * @param b Second date
 * @returns Difference in milliseconds
 */
export function difference (a: Date, b: Date) {
    return moment(a).diff(b);
}

/**
 * Formats a date object using .NET's default formatting.
 * @param date Date
 * @returns Date in .NET's default formatting.
 */
export function formatDateComponentAsDotNetString (date: Date) {
    return moment(date).format('YYYY-MM-DDThh:mm:ss.SSSSSSSZ');
}

/**
 * Formats a UTC timestamp string as an English-localized date value in local time.
 * @param timestampUtc UTC timestamp
 * @returns Local equivalent of UTC date component in English locale.
 */
export function formatDateComponentAsLocalInEnglishLocale (date: Date) {
    return moment(date).format('l');
}

/**
 * Formats a date object as a weekday + calendar date pair in local time.
 * @param date Date
 * @returns Date in weekday + calendar date format.
 */
export function formatDateComponentAsWeekdayAndDateFormat (date: Date) {
    const time = moment(date);

    return `${time.format('dddd')}, ${time.format('l')}`;
}

/**
 * Formats a UTC timestamp string as an English-localized timestamp in local time.
 * @param timestampUtc UTC timestamp
 * @param includeTimezone Whether the timezone offset should be included in the formatted timestamp
 * @returns Local equivalent of UTC timestamp in English locale.
 */
export function formatUtcAsLocalInEnglishLocale (timestampUtc: string, includeTimezone = false) {
    const time = moment.utc(timestampUtc).local();

    if (includeTimezone) {
        return `${time.format('l')}, ${time.format('LT')} ${time.format('Z')}`;
    }

    return `${time.format('l')}, ${time.format('LT')}`;
}

/**
 * Formats a UTC timestamp string as a weekday + calendar date pair in local time.
 * @param timestampUtc UTC timestamp
 * @returns Local equivalent of UTC timestamp in weekday + calendar date format.
 */
export function formatUtcAsLocalInWeekdayAndDateFormat (timestampUtc: string) {
    const time = moment.utc(timestampUtc).local();

    return `${time.format('dddd')}, ${time.format('l')}`;
}

/**
 * Gets local Javascript Date representation of given UTC timestamp.
 * @param timestampUtc UTC timestamp
 * @returns Date object
 */
export function getLocalDateFromUtcTimestamp (timestampUtc: string) {
    return moment.utc(timestampUtc).local().toDate();
}

/**
 * Get date component in local time from UTC timestamp.
 * @param timestampUtc Timestamp as string
 * @returns Date component of timestamp
 */
export function getLocalDateComponentFromUtcTimestamp (timestampUtc: string) {
    const formattedDatestamp = moment(timestampUtc).format('YYYY-MM-DD');

    return moment(formattedDatestamp)
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate();
}

/**
 * Get time component in local time from UTC timestamp.
 * @param timestampUtc Timestamp as string
 * @returns Date component of timestamp
 */
export function getLocalTimeComponentFromUtcTimestamp (timestampUtc: string) {
    return moment(timestampUtc).format('HH:mm');
}

/**
 * Gets standardized string representation of a time range value. Primarily used to coerce case-insensitive
 * equivalents into the proper casing.
 * @param value Value to check
 * @returns Normalized time range value if it is a valid value, undefined otherwise
 */
export function getNormalizedTimeRangeValue (value: string): TimeRangeValue | undefined {
    if (value.toLowerCase() === Last24Hours.toLowerCase()) {
        return Last24Hours;
    }

    if (value.toLowerCase() === Last30Days.toLowerCase()) {
        return Last30Days;
    }

    if (value.toLowerCase() === Last48Hours.toLowerCase()) {
        return Last48Hours;
    }

    if (value.toLowerCase() === Last60Days.toLowerCase()) {
        return Last60Days;
    }

    if (value.toLowerCase() === Last7Days.toLowerCase()) {
        return Last7Days;
    }

    if (value.toLowerCase() === Last90Days.toLowerCase()) {
        return Last90Days;
    }

    return undefined;
}

/**
 * Gets UTC Javascript Date representation of given UTC timestamp.
 * @param timestampUtc UTC timestamp
 * @returns Date object
 */
export function getUtcDateFromUtcTimestamp (timestampUtc: string) {
    return moment.utc(timestampUtc).toDate();
}

/**
 * Initialize date from date component and time component.
 * @param date Date component
 * @param time Time component as string
 * @returns Date
 */
// TODO: rename
export function initializeDate (date: Date, time: string) {
    // Create timestamp to handle UTC conversion
    const localTimestamp = moment()
        .year(date.getFullYear())
        .month(date.getMonth())
        .date(date.getDate())
        .hour(parseInt(time.split(':')[0]))
        .minute(parseInt(time.split(':')[1]));

    return moment.utc(localTimestamp).toDate();
}

/**
 * Checks whether or not the given string value is a valid time range value.
 * @param value Value to check
 * @param ignoreCase Whether or not check should ignore case. Default is false.
 * @returns Whether or not passed value is a valid time range value
 */
export function isValidTimeRangeValue (value: string, ignoreCase = false) {
    const compareValue = ignoreCase ? value.toLowerCase() : value;

    return compareValue === (ignoreCase ? Last24Hours.toLowerCase() : Last24Hours)
        && compareValue === (ignoreCase ? Last30Days.toLowerCase() : Last30Days)
        && compareValue === (ignoreCase ? Last48Hours.toLowerCase() : Last48Hours)
        && compareValue === (ignoreCase ? Last60Days.toLowerCase() : Last60Days)
        && compareValue === (ignoreCase ? Last7Days.toLowerCase() : Last7Days)
        && compareValue === (ignoreCase ? Last90Days.toLowerCase() : Last90Days);
}
